import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ListTemplate, FormTemplate } from '../types/formBuilder';
import { FormSubmission, OptimizedFormSubmissionService } from '../services/optimizedFormSubmissionService';
import { TemplateService } from '../services/templateService';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LoadingScreen from '../components/common/LoadingScreen';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { Button } from '../components/common/Button';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  TableCellsIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { hu, enUS } from 'date-fns/locale';
import { toast } from 'react-hot-toast';

const FormSubmissionsList: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  // State
  const [listTemplate, setListTemplate] = useState<ListTemplate | null>(null);
  const [connectedForm, setConnectedForm] = useState<FormTemplate | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [totalCount, setTotalCount] = useState(0);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Load data
  useEffect(() => {
    if (listId) {
      loadListTemplate();
    }
  }, [listId]);

  useEffect(() => {
    if (listTemplate) {
      loadSubmissions();
      loadSubmissionCount();
    }
  }, [listTemplate, sortField, sortDirection, currentPage]);

  const loadListTemplate = async () => {
    if (!listId) return;
    
    try {
      setLoading(true);
      const template = await TemplateService.getListTemplate(listId);
      setListTemplate(template);
      
      // Load connected form if exists
      if (template.formTemplateId) {
        try {
          const formTemplate = await TemplateService.getFormTemplate(template.formTemplateId);
          setConnectedForm(formTemplate);
        } catch (error) {
          console.error('Error loading connected form:', error);
        }
      }
      
      // Set default sort field
      setSortField(template.settings.defaultSort?.field || 'createdAt');
      setSortDirection(template.settings.defaultSort?.direction || 'desc');
    } catch (error) {
      console.error('Error loading list template:', error);
      toast.error(t('formBuilder.listViewer.loadError') || 'Failed to load list');
      navigate('/form-builder');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    if (!listTemplate?.dataSource.formTemplateId) return;
    
    setLoading(true);
    try {
      const result = await OptimizedFormSubmissionService.getFormSubmissions({
        formTemplateId: listTemplate.dataSource.formTemplateId,
        organizationId: user?.organizationId,
        pageSize: listTemplate.settings.pageSize || 25,
        sortField,
        sortDirection
      });
      
      setSubmissions(result.submissions);
      setHasMore(result.hasMore);
    } catch (error) {
      console.error('Error loading submissions:', error);
      toast.error(t('formBuilder.listViewer.loadSubmissionsError') || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionCount = async () => {
    if (!listTemplate?.dataSource.formTemplateId) return;
    
    try {
      const count = await OptimizedFormSubmissionService.getSubmissionCount(
        listTemplate.dataSource.formTemplateId,
        user?.organizationId
      );
      setTotalCount(count);
    } catch (error) {
      console.error('Error loading submission count:', error);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const formatCellValue = (value: any, columnType: string): string => {
    if (value === null || value === undefined) return '-';
    
    switch (columnType) {
      case 'datetime':
        const formatted = formatDistanceToNow(new Date(value), {
          addSuffix: true,
          locale: language === 'hu' ? hu : enUS
        });
        
        if (language === 'hu') {
          return formatted
            .replace('körülbelül ', '')
            .replace(' ezelőtt', '')
            .replace('órával', 'órája')
            .replace('nappal', 'napja')
            .replace('héttel', 'hete')
            .replace('hónappal', 'hónapja')
            .replace('évvel', 'éve')
            .replace('perccel', 'perce')
            .replace('másodperccel', 'másodperce');
        }
        
        return formatted;
      case 'date':
        return new Date(value).toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-US');
      case 'currency':
        return new Intl.NumberFormat(language === 'hu' ? 'hu-HU' : 'en-US', {
          style: 'currency',
          currency: 'HUF'
        }).format(value);
      case 'number':
        return new Intl.NumberFormat(language === 'hu' ? 'hu-HU' : 'en-US').format(value);
      case 'boolean':
        return value ? '✓' : '✗';
      case 'user':
        return value || '-';
      case 'link':
        return value ? '📎' : '-';
      default:
        // Handle objects and arrays
        if (typeof value === 'object') {
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          return JSON.stringify(value);
        }
        return String(value);
    }
  };

  const getFieldValue = (submission: FormSubmission, fieldName: string): any => {
    // Handle metadata fields
    switch (fieldName) {
      case 'submittedAt':
      case 'createdAt':
        return submission.createdAt;
      case 'submittedByName':
        return submission.submittedByName || submission.submittedBy;
      case 'status':
        return submission.status;
      default:
        // Handle data.fieldId format (e.g., "data.text_1760249723411")
        if (fieldName.startsWith('data.')) {
          const actualFieldName = fieldName.substring(5); // Remove "data." prefix
          return submission.data?.[actualFieldName];
        }
        return submission.data?.[fieldName];
    }
  };

  const handleViewSubmission = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
  };

  const getFieldLabel = (fieldId: string): string => {
    if (!connectedForm) return fieldId;
    const field = connectedForm.fields.find(f => f.id === fieldId);
    return field?.label || field?.name || fieldId;
  };

  const handleEditSubmission = (submission: FormSubmission) => {
    navigate(`/form-filler/${submission.templateId}?submissionId=${submission.id}`);
  };

  const handleExport = () => {
    try {
      // Filter submissions by status if needed
      const filteredData = statusFilter === 'all' 
        ? submissions 
        : submissions.filter(s => s.status === statusFilter);

      // Create CSV content
      const headers = ['ID', 'Form Name', 'Status', 'Submitted By', 'Submitted At'];
      
      // Add form field headers if connected form exists
      if (connectedForm) {
        connectedForm.fields.forEach(field => {
          headers.push(field.label || field.name);
        });
      }

      const csvRows = [headers.join(',')];

      filteredData.forEach(submission => {
        const row = [
          submission.id,
          `"${submission.templateName}"`,
          submission.status,
          `"${submission.submittedByName || submission.submittedBy}"`,
          new Date(submission.createdAt).toLocaleString()
        ];

        // Add form field data
        if (connectedForm) {
          connectedForm.fields.forEach(field => {
            const value = submission.data?.[field.id] || '';
            row.push(`"${String(value).replace(/"/g, '""')}"`);
          });
        }

        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `submissions_${listTemplate?.name}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(t('formBuilder.listViewer.exportSuccess') || 'Export successful');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('formBuilder.listViewer.exportError') || 'Export failed');
    }
  };

  // Filter submissions by status
  const filteredSubmissions = statusFilter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === statusFilter);

  if (loading && !listTemplate) {
    return <LoadingScreen />;
  }

  if (!listTemplate) {
    return (
      <div className="space-y-6">
        <CommonPageHeader
          title={t('formBuilder.listViewer.notFound') || 'List not found'}
          subtitle={t('formBuilder.listViewer.notFoundDescription') || 'The requested list could not be found'}
          icon={TableCellsIcon}
        >
          <button
            onClick={() => navigate('/form-builder')}
            className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            <span>{t('common.buttons.back') || 'Back'}</span>
          </button>
        </CommonPageHeader>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              {t('formBuilder.listViewer.notFound') || 'List not found'}
            </p>
            <Button onClick={() => navigate('/form-builder')}>
              {t('common.buttons.backToBuilder') || 'Back to Form Builder'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Filter visible columns
  const visibleColumns = listTemplate.columns
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .slice(0, 10); // Limit to 10 columns for better UX

  const pageSize = listTemplate.settings.pageSize || 25;
  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <CommonPageHeader
        title={listTemplate.name}
        subtitle={connectedForm ? 
          (language === 'hu' ? 
            `${totalCount} ${totalCount === 1 ? (t('formBuilder.listViewer.submission') || 'beküldés') : (t('formBuilder.listViewer.submissions') || 'beküldés')} - "${connectedForm.name}" űrlapból` :
            `${totalCount} ${totalCount === 1 ? (t('formBuilder.listViewer.submission') || 'submission') : (t('formBuilder.listViewer.submissions') || 'submissions')} from "${connectedForm.name}"`
          ) :
          `${totalCount} ${totalCount === 1 ? (t('formBuilder.listViewer.submission') || 'submission') : (t('formBuilder.listViewer.submissions') || 'submissions')}`
        }
        icon={TableCellsIcon}
      >
        <button
          onClick={() => navigate('/form-builder')}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          <span>{t('common.buttons.back') || 'Back'}</span>
        </button>
      </CommonPageHeader>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {listTemplate.settings.showSearch && (
                <div className="relative">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder={t('formBuilder.listViewer.search') || 'Search...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {listTemplate.settings.showFilters && (
                <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  {t('formBuilder.listViewer.filters') || 'Szűrők'}
                </Button>
              )}
              
              {listTemplate.settings.showExport && (
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  {t('formBuilder.listViewer.export') || 'Exportálás'}
                </Button>
              )}
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="p-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Státusz:
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">Összes</option>
                  <option value="draft">Piszkozat</option>
                  <option value="submitted">Beküldve</option>
                  <option value="approved">Jóváhagyva</option>
                  <option value="rejected">Elutasítva</option>
                </select>
                {statusFilter !== 'all' && (
                  <button
                    onClick={() => setStatusFilter('all')}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    Szűrők törlése
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : filteredSubmissions.length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-2">
                    {t('formBuilder.listViewer.noData') || 'No submissions found'}
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500">
                    {t('formBuilder.listViewer.noDataDescription') || 'Submissions will appear here once the form is filled out'}
                  </p>
                </div>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {visibleColumns.map((column) => (
                      <th
                        key={column.id}
                        className={`px-3 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300 ${
                          column.sortable ? 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600' : ''
                        }`}
                        onClick={() => column.sortable && handleSort(column.field)}
                        style={{ width: column.width ? `${column.width}px` : 'auto' }}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{column.label}</span>
                          {column.sortable && sortField === column.field && (
                            sortDirection === 'asc' ? 
                              <ArrowUpIcon className="h-3 w-3" /> : 
                              <ArrowDownIcon className="h-3 w-3" />
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300 w-20">
                      {t('formBuilder.listViewer.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredSubmissions.map((submission) => (
                    <tr key={submission.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      {visibleColumns.map((column) => (
                        <td
                          key={column.id}
                          className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                        >
                          {formatCellValue(getFieldValue(submission, column.field), column.type)}
                        </td>
                      ))}
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          <button
                            onClick={() => handleViewSubmission(submission)}
                            className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title={t('common.buttons.view') || 'View'}
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          
                          {submission.status === 'draft' && (
                            <button
                              onClick={() => handleEditSubmission(submission)}
                              className="p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                              title={t('common.buttons.edit') || 'Edit'}
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-gray-500 dark:text-gray-400 mb-2">
                  {t('formBuilder.listViewer.noData') || 'No submissions found'}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {t('formBuilder.listViewer.noDataDescription') || 'Submissions will appear here once the form is filled out'}
                </p>
              </div>
            </div>
          ) : (
            filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="space-y-3">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        submission.status === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                        submission.status === 'submitted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                        submission.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                        'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {submission.status}
                      </span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleViewSubmission(submission)}
                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title={t('common.buttons.view') || 'View'}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      
                      {submission.status === 'draft' && (
                        <button
                          onClick={() => handleEditSubmission(submission)}
                          className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                          title={t('common.buttons.edit') || 'Edit'}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Main Fields */}
                  {visibleColumns.slice(0, 4).map((column) => {
                    const value = getFieldValue(submission, column.field);
                    if (!value && value !== 0 && value !== false) return null;
                    
                    return (
                      <div key={column.id} className="flex justify-between items-start">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {column.label}:
                        </span>
                        <span className="text-sm text-gray-900 dark:text-gray-100 text-right max-w-[60%] break-words">
                          {formatCellValue(value, column.type)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Submitted Info */}
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                    <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>{t('formBuilder.listViewer.submittedBy') || 'Submitted by'}: {submission.submittedByName || submission.submittedBy}</span>
                      <span>{formatCellValue(submission.createdAt, 'datetime')}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-600 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex-1 flex justify-between sm:hidden">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.buttons.previous') || 'Previous'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                {t('common.buttons.next') || 'Next'}
              </Button>
            </div>
            
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('formBuilder.listViewer.showing') || 'Showing'}{' '}
                  <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span>
                  {' '}{t('formBuilder.listViewer.to') || 'to'}{' '}
                  <span className="font-medium">{Math.min(currentPage * pageSize, totalCount)}</span>
                  {' '}{t('formBuilder.listViewer.of') || 'of'}{' '}
                  <span className="font-medium">{totalCount}</span>
                  {' '}{t('formBuilder.listViewer.results') || 'results'}
                </p>
              </div>
              
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('common.buttons.previous') || 'Previous'}</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === currentPage
                            ? 'z-10 bg-primary-50 dark:bg-primary-900/20 border-primary-500 text-primary-600 dark:text-primary-400'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">{t('common.buttons.next') || 'Next'}</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Submission Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedSubmission(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('formBuilder.listViewer.submissionDetails') || 'Submission Details'}
                </h2>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customerPortal.submissions.formName') || 'Form Name'}
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100">
                    {selectedSubmission.templateName}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('common.status')}
                  </label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedSubmission.status === 'draft' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' :
                    selectedSubmission.status === 'submitted' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                    selectedSubmission.status === 'approved' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                    'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  }`}>
                    {selectedSubmission.status}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('formBuilder.listViewer.submittedBy') || 'Submitted By'}
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100">
                    {selectedSubmission.submittedByName || selectedSubmission.submittedBy}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('customerPortal.submissions.submissionDate') || 'Submission Date'}
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100">
                    {formatCellValue(selectedSubmission.createdAt, 'datetime')}
                  </p>
                </div>

                {/* Submitted Data */}
                {selectedSubmission.data && Object.keys(selectedSubmission.data).length > 0 && (
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {t('customerPortal.submissions.submittedData') || 'Submitted Data'}
                    </label>
                    <div className="space-y-3">
                      {Object.entries(selectedSubmission.data).map(([fieldId, value]) => (
                        <div key={fieldId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                            {getFieldLabel(fieldId)}
                          </p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">
                            {typeof value === 'boolean' ? (value ? '✓ Yes' : '✗ No') : 
                             typeof value === 'object' ? JSON.stringify(value) : 
                             String(value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {t('common.close') || 'Close'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSubmissionsList;